import { useEffect, useState } from "react";
import { Plus, Trash2, Send, Instagram, Facebook, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Modal from "@/components/site/Modal";

export default function SocialScheduler() {
  const [posts, setPosts] = useState([]);
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [openRule, setOpenRule] = useState(false);
  const [f, setF] = useState({ caption: "", media_url: "", post_type: "post", platforms: ["instagram"], scheduled_for: "" });
  const [r, setR] = useState({ name: "", trigger_keyword: "", reply_text: "", platform: "instagram", enabled: true });
  const load = async () => {
    const [p, dm] = await Promise.all([api.get("/admin/social/posts"), api.get("/admin/social/dm-rules")]);
    setPosts(p.data); setRules(dm.data);
  };
  useEffect(() => { load(); }, []);

  const togglePlat = (p) => setF((s) => ({ ...s, platforms: s.platforms.includes(p) ? s.platforms.filter((x) => x !== p) : [...s.platforms, p] }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const dt = new Date(f.scheduled_for).toISOString();
      await api.post("/admin/social/posts", { ...f, scheduled_for: dt });
      toast.success("Post scheduled");
      setOpen(false);
      setF({ caption: "", media_url: "", post_type: "post", platforms: ["instagram"], scheduled_for: "" });
      load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/social/posts/${id}`); load(); };
  const publishNow = async (id) => { await api.post(`/admin/social/posts/${id}/publish-now`); toast.success("Published (mock)"); load(); };

  const saveRule = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/social/dm-rules", r);
      toast.success("DM rule saved");
      setOpenRule(false); setR({ name: "", trigger_keyword: "", reply_text: "", platform: "instagram", enabled: true }); load();
    } catch { toast.error("Save failed"); }
  };
  const delRule = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/social/dm-rules/${id}`); load(); };

  return (
    <div data-testid="admin-social">
      <div className="flex items-center justify-between mb-3">
        <div><div className="label-mono mb-2">/ Automation</div><h1 className="font-display text-4xl font-bold tracking-tighter">Social Scheduler</h1></div>
        <div className="flex gap-2">
          <button onClick={() => setOpenRule(true)} className="glass-btn" data-testid="dmrule-new"><MessageSquare size={14}/> DM Rule</button>
          <button onClick={() => setOpen(true)} className="glass-btn solid" data-testid="post-new"><Plus size={14}/> Schedule Post</button>
        </div>
      </div>
      <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 mb-8 text-amber-300 text-sm">
        ⚡ MOCK MODE — Posts and DM rules are stored locally. Connect real Meta tokens once your Facebook App passes review.
      </div>

      <div className="mb-12">
        <div className="label-mono mb-3 text-cyan-400">/ Scheduled Posts ({posts.length})</div>
        <div className="grid md:grid-cols-2 gap-4">
          {posts.map((p) => (
            <div key={p.id} className="p-5 rounded-2xl border border-white/5 bg-[#121215]" data-testid={`post-${p.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-2">
                  {p.platforms.includes("instagram") && <span className="badge"><Instagram size={10}/> IG</span>}
                  {p.platforms.includes("facebook") && <span className="badge"><Facebook size={10}/> FB</span>}
                  <span className="badge muted">{p.post_type}</span>
                </div>
                <span className={`badge ${p.status === "published" ? "success" : "muted"}`}>{p.status}</span>
              </div>
              {p.media_url && <img src={p.media_url} alt="" className="w-full h-48 object-cover rounded-lg mb-3"/>}
              <p className="text-sm text-zinc-300 line-clamp-3 mb-3">{p.caption}</p>
              <div className="text-zinc-500 text-xs font-mono mb-3">📅 {new Date(p.scheduled_for).toLocaleString()}</div>
              <div className="flex justify-end gap-2">
                {p.status === "scheduled" && <button onClick={() => publishNow(p.id)} className="glass-btn ghost p-2" title="Publish now"><Send size={12}/></button>}
                <button onClick={() => del(p.id)} className="glass-btn ghost p-2"><Trash2 size={12}/></button>
              </div>
            </div>
          ))}
          {posts.length === 0 && <div className="text-zinc-500 text-sm col-span-2 text-center py-8">No posts scheduled.</div>}
        </div>
      </div>

      <div>
        <div className="label-mono mb-3 text-cyan-400">/ DM Auto-Reply Rules ({rules.length})</div>
        <p className="text-zinc-500 text-xs mb-3">Replies automatically to incoming DMs that contain the trigger keyword. Compliant with Meta policy (responds only to user-initiated messages within 24h).</p>
        <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
          {rules.map((rule) => (
            <div key={rule.id} className="px-5 py-4 border-b border-white/5 last:border-0" data-testid={`dmrule-${rule.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <span className="font-display font-semibold">{rule.name}</span>
                  <span className="badge muted ml-3">{rule.platform}</span>
                  {rule.enabled ? <span className="badge success ml-2">ON</span> : <span className="badge muted ml-2">OFF</span>}
                </div>
                <button onClick={() => delRule(rule.id)} className="glass-btn ghost p-2"><Trash2 size={12}/></button>
              </div>
              <div className="text-xs text-zinc-500 font-mono">when message contains: <span className="text-cyan-300">"{rule.trigger_keyword}"</span></div>
              <div className="text-sm text-zinc-300 mt-1">→ {rule.reply_text}</div>
            </div>
          ))}
          {rules.length === 0 && <div className="px-5 py-6 text-zinc-500 text-sm text-center">No DM rules. Add one to auto-reply to common questions.</div>}
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Schedule Post" testid="post-modal">
        <form onSubmit={save} className="space-y-3" data-testid="post-form">
          <div>
            <label className="label-mono block mb-2">Post Type</label>
            <div className="flex gap-2">
              {["post","reel","story"].map((t) => (
                <button key={t} type="button" onClick={() => setF({ ...f, post_type: t })}
                        className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider border ${f.post_type === t ? "bg-cyan-500/15 border-cyan-400 text-cyan-300" : "border-white/10 text-zinc-400"}`}
                        data-testid={`post-type-${t}`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-mono block mb-2">Platforms</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => togglePlat("instagram")}
                      className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider border flex items-center gap-2 ${f.platforms.includes("instagram") ? "bg-cyan-500/15 border-cyan-400 text-cyan-300" : "border-white/10 text-zinc-400"}`}>
                <Instagram size={12}/> Instagram
              </button>
              <button type="button" onClick={() => togglePlat("facebook")}
                      className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider border flex items-center gap-2 ${f.platforms.includes("facebook") ? "bg-cyan-500/15 border-cyan-400 text-cyan-300" : "border-white/10 text-zinc-400"}`}>
                <Facebook size={12}/> Facebook
              </button>
            </div>
          </div>
          <div><label className="label-mono block mb-2">Media URL (image / video)</label><input className="pulse-input" placeholder="https://..." value={f.media_url} onChange={(e) => setF({ ...f, media_url: e.target.value })} data-testid="post-media"/></div>
          <div><label className="label-mono block mb-2">Caption</label><textarea className="pulse-input pulse-textarea" value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} data-testid="post-caption"/></div>
          <div><label className="label-mono block mb-2">Scheduled For *</label><input type="datetime-local" required className="pulse-input" value={f.scheduled_for} onChange={(e) => setF({ ...f, scheduled_for: e.target.value })} data-testid="post-when"/></div>
          <button className="glass-btn solid w-full justify-center" data-testid="post-save">Schedule</button>
        </form>
      </Modal>

      <Modal open={openRule} onClose={() => setOpenRule(false)} title="DM Auto-Reply Rule" testid="dmrule-modal">
        <form onSubmit={saveRule} className="space-y-3" data-testid="dmrule-form">
          <div><label className="label-mono block mb-2">Rule Name *</label><input required className="pulse-input" placeholder='e.g. "Distribution Inquiry"' value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} data-testid="dmrule-name"/></div>
          <div><label className="label-mono block mb-2">Trigger Keyword *</label><input required className="pulse-input" placeholder='e.g. "DISTRO"' value={r.trigger_keyword} onChange={(e) => setR({ ...r, trigger_keyword: e.target.value })} data-testid="dmrule-keyword"/></div>
          <div><label className="label-mono block mb-2">Auto-Reply Text *</label><textarea required className="pulse-input pulse-textarea" value={r.reply_text} onChange={(e) => setR({ ...r, reply_text: e.target.value })} placeholder="Thanks for reaching out! Submit at pulsemedia.vip/distribution" data-testid="dmrule-reply"/></div>
          <div>
            <label className="label-mono block mb-2">Platform</label>
            <select className="pulse-input" value={r.platform} onChange={(e) => setR({ ...r, platform: e.target.value })}>
              <option value="instagram">Instagram</option><option value="facebook">Facebook</option>
            </select>
          </div>
          <button className="glass-btn solid w-full justify-center" data-testid="dmrule-save">Save Rule</button>
        </form>
      </Modal>
    </div>
  );
}
